import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link2, Pencil } from "lucide-react";
import type { CampaignVariableMappingEntry } from "@multi-chat/shared";
import { LinkifiedText } from "../../components/common/LinkifiedText/index.js";
import { cn } from "../../lib/utils.js";
import {
  campaignVariableTokenId,
  mappingEntryLabel,
  variableTokenLabel,
  type CampaignVariableTokenIdentity,
  type TemplatePreviewSegment,
} from "./campaign-message-presentation.js";

export type CampaignVariableActivate = (parameterKey: string, tokenId: string) => void;

/**
 * One clickable variable in its real preview position.
 * Unconfigured: no mapping yet. Configured: mapping exists; shows the sample
 * value, or the source label with a marker when the sample recipient has none.
 */
export function CampaignVariableToken({
  identity,
  entry,
  sampleValue,
  canEdit,
  onActivate,
  variant = "inline",
}: {
  identity: CampaignVariableTokenIdentity;
  entry: CampaignVariableMappingEntry | null;
  sampleValue: string | null;
  canEdit: boolean;
  onActivate: CampaignVariableActivate;
  variant?: "inline" | "button";
}): React.JSX.Element {
  const { t } = useTranslation();
  const tokenId = campaignVariableTokenId(identity);
  const token = variableTokenLabel(identity.parameterKey);
  const configured = entry != null;
  const valueMissing = configured && sampleValue == null;
  const label = !configured ? token : (sampleValue ?? mappingEntryLabel(t, entry));
  const component = t(`campaigns.content.components.${identity.componentType}`, {
    index: (identity.buttonIndex ?? 0) + 1,
  });

  return (
    <button
      type="button"
      disabled={!canEdit}
      className={cn(
        "group mx-0.5 inline-flex max-w-full items-center gap-1 rounded-full border px-1.5 py-px align-baseline font-medium leading-[1.4] transition-colors",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-1",
        variant === "button" ? "text-[0.6875rem]" : "text-[0.8125rem]",
        configured
          ? "border-accent/30 bg-accent-soft text-ink"
          : "border-dashed border-warning/60 bg-warning-muted text-warning",
        canEdit
          ? configured
            ? "cursor-pointer hover:border-accent/70"
            : "cursor-pointer hover:border-warning"
          : "cursor-default",
      )}
      onClick={() => onActivate(identity.parameterKey, tokenId)}
      aria-label={t("campaigns.content.editVariable", { token, component })}
      data-testid={`campaign-variable-chip-${identity.parameterKey}`}
      data-token-id={tokenId}
      data-configured={configured ? "true" : "false"}
      data-mapped={configured && !valueMissing ? "true" : "false"}
    >
      {variant === "button" ? <Link2 size={11} className="shrink-0" aria-hidden="true" /> : null}
      <span className="min-w-0 max-w-[14rem] truncate" dir="auto">
        {label}
      </span>
      {valueMissing ? (
        <span className="size-1.5 shrink-0 rounded-full bg-warning" aria-hidden="true" />
      ) : null}
      {canEdit ? (
        <Pencil
          size={10}
          className="shrink-0 opacity-40 transition-opacity group-hover:opacity-80 group-focus-visible:opacity-80"
          aria-hidden="true"
        />
      ) : null}
    </button>
  );
}

export function CampaignPreviewSegments({
  segments,
  entries,
  canEdit,
  onActivate,
}: {
  segments: readonly TemplatePreviewSegment[];
  entries: Readonly<Record<string, CampaignVariableMappingEntry>>;
  canEdit: boolean;
  onActivate: CampaignVariableActivate;
}): React.JSX.Element {
  const nodes: ReactNode[] = [];
  segments.forEach((segment, index) => {
    if (segment.kind === "text") {
      nodes.push(<LinkifiedText key={`text-${index}`} text={segment.text} />);
      return;
    }
    nodes.push(
      <CampaignVariableToken
        key={`var-${segment.parameterKey}-${segment.occurrence}`}
        identity={{
          componentType: segment.componentType,
          parameterKey: segment.parameterKey,
          occurrence: segment.occurrence,
        }}
        entry={entries[segment.parameterKey] ?? null}
        sampleValue={segment.mapped ? segment.display : null}
        canEdit={canEdit}
        onActivate={onActivate}
      />,
    );
  });
  return <>{nodes}</>;
}
